import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  Calendar,
  Check,
  Clock,
  Copy,
  ExternalLink,
  FileText,
  GraduationCap,
  History,
  Info,
  Loader2,
  MapPin,
  Save,
  User,
  Users,
  Video,
  X,
  XCircle,
} from "lucide-react";
import { Button } from "src/components/ui/Button";
import { Input } from "src/components/ui/Input";
import { Label } from "src/components/ui/Label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "src/components/ui/Select";
import { Textarea } from "src/components/ui/TextArea";
import { Card, CardContent, CardFooter } from "src/components/ui/Card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "src/components/ui/tabs";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "src/components/ui/tooltip";
import ConfirmationModal from "src/components/ui/ConfirmationModal";
import {
  PersonCommandCombobox,
  PersonCommandOption,
} from "src/components/ui/PersonCommandCombobox";
import { usePageHeaderStore } from "src/store/pageHeaderStore";
import { useToast } from "src/hooks/use-toast";
import { useSchedulingParticipants } from "src/hooks/useSchedulingParticipants";
import { SchedulingParticipant } from "src/types/scheduling";
import {
  SessionParticipantData,
  SessionParticipantWriteData,
  SessionStatus,
  sessionsService,
} from "src/services/sessionsService";

interface EditSessionForm {
  date: string;
  startTime: string;
  duration: string;
  status: SessionStatus;
  location: string;
  meetingUrl: string;
  tutorId: string;
  studentId: string;
  notes: string;
}

const toDateInput = (value: string) => {
  const date = new Date(value);
  return `${date.getFullYear()}-${`${date.getMonth() + 1}`.padStart(2, "0")}-${`${date.getDate()}`.padStart(2, "0")}`;
};

const toTimeInput = (value: string) => {
  const date = new Date(value);
  return `${`${date.getHours()}`.padStart(2, "0")}:${`${date.getMinutes()}`.padStart(2, "0")}`;
};

const toScheduledAt = (date: string, time: string) => new Date(`${date}T${time}:00`).toISOString();

const mapParticipantToOption = (participant: SchedulingParticipant): PersonCommandOption => ({
  value: participant.id,
  label: [participant.firstName, participant.lastName].filter(Boolean).join(" ") || participant.email,
  description: participant.description,
  keywords: participant.keywords,
  recent: participant.recent,
});

const getStatusBadgeClass = (status: SessionStatus) => {
  switch (status) {
    case "scheduled":
      return "bg-emerald-50 text-emerald-700 border-emerald-200";
    case "started":
      return "bg-blue-50 text-blue-700 border-blue-200";
    case "ended":
      return "bg-purple-50 text-purple-700 border-purple-200";
    case "cancelled":
      return "bg-rose-50 text-rose-700 border-rose-200";
    case "failed":
      return "bg-amber-50 text-amber-800 border-amber-200";
    default:
      return "bg-slate-100 text-slate-700 border-slate-200";
  }
};

const EditSession = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { setTitle, setDescription, setActions, reset } = usePageHeaderStore();
  const sessionId = searchParams.get("id");
  const [form, setForm] = useState<EditSessionForm | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [additionalStudentIds, setAdditionalStudentIds] = useState<string[]>([]);
  const [copiedIcs, setCopiedIcs] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);

  const {
    data: session,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["admin", "session", sessionId],
    queryFn: async () => {
      if (!sessionId) throw new Error("Missing session ID.");
      const result = await sessionsService.getSessionById(sessionId);
      if (!result) throw new Error("Session not found.");
      return result;
    },
    enabled: Boolean(sessionId),
  });

  const { data: schedulingParticipants, isLoading: isParticipantsLoading } = useSchedulingParticipants();
  const {
    data: roster = [],
    isLoading: isRosterLoading,
  } = useQuery({
    queryKey: ["admin", "session-participants", sessionId],
    queryFn: () => sessionsService.getSessionParticipants(sessionId!),
    enabled: Boolean(sessionId),
  });

  const studentOptions = useMemo(
    () => (schedulingParticipants?.students || []).map(mapParticipantToOption),
    [schedulingParticipants?.students]
  );
  const tutorOptions = useMemo(
    () => (schedulingParticipants?.tutors || []).map(mapParticipantToOption),
    [schedulingParticipants?.tutors]
  );

  useEffect(() => {
    setTitle("Edit Session");
    setDescription("Modify real session details, assignments, and schedule");
    setActions(
      <Button
        variant="outline"
        size="sm"
        onClick={() => navigate("/admin/sessions/list")}
        className="flex items-center gap-1"
      >
        <ArrowLeft size={16} />
        Back to Sessions
      </Button>
    );

    return () => reset();
  }, [setTitle, setDescription, setActions, reset, navigate]);

  useEffect(() => {
    if (!session) return;

    setForm({
      date: toDateInput(session.scheduled_at),
      startTime: toTimeInput(session.scheduled_at),
      duration: `${session.duration_min}`,
      status: session.status,
      location: session.location || "",
      meetingUrl: session.meeting_url || session.meeting_session?.meeting_url || "",
      tutorId: session.tutor_id,
      studentId: session.student_id || "",
      notes: session.session_details?.notes || "",
    });
  }, [session]);

  useEffect(() => {
    if (!session) return;

    setAdditionalStudentIds(
      roster
        .filter(
          (participant) =>
            participant.participation_status === "active" &&
            participant.participant_role === "student" &&
            participant.user_id !== session.student_id
        )
        .map((participant) => participant.user_id)
    );
  }, [roster, session]);

  const handleChange = <K extends keyof EditSessionForm>(field: K, value: EditSessionForm[K]) => {
    setForm((current) => (current ? { ...current, [field]: value } : current));
  };

  const activeStudentIds = useMemo(
    () => Array.from(new Set([form?.studentId || "", ...additionalStudentIds].filter(Boolean))),
    [form?.studentId, additionalStudentIds]
  );
  const additionalStudentOptions = studentOptions.filter(
    (option) => !activeStudentIds.includes(option.value)
  );
  const getStudentLabel = (studentId: string) =>
    studentOptions.find((option) => option.value === studentId)?.label ||
    (session?.student_profile
      ? `${session.student_profile.first_name} ${session.student_profile.last_name}`
      : "Selected student");

  const getTutorLabel = (tutorId: string) =>
    tutorOptions.find((option) => option.value === tutorId)?.label ||
    (session?.tutor_profile
      ? `${session.tutor_profile.first_name} ${session.tutor_profile.last_name}`
      : "Selected tutor");

  const participantCount = (form?.tutorId ? 1 : 0) + activeStudentIds.length;

  const tutorTimezone =
    schedulingParticipants?.tutors.find((t) => t.id === form?.tutorId)?.timezone ||
    session?.tutor_profile?.timezone ||
    Intl.DateTimeFormat().resolvedOptions().timeZone;

  const studentTimezone =
    schedulingParticipants?.students.find((s) => s.id === form?.studentId)?.timezone ||
    Intl.DateTimeFormat().resolvedOptions().timeZone;

  const handlePrimaryStudentChange = (studentId: string) => {
    handleChange("studentId", studentId);
    setAdditionalStudentIds((current) => current.filter((id) => id !== studentId));
  };

  const handleAdditionalStudentChange = (studentId: string) => {
    if (!studentId || studentId === form?.studentId) return;
    setAdditionalStudentIds((current) =>
      current.includes(studentId) ? current : [...current, studentId]
    );
  };

  const buildDesiredRoster = (): Omit<SessionParticipantWriteData, "added_by">[] => {
    if (!form) return [];

    const selectedUserIds = new Set([form.tutorId, ...activeStudentIds]);
    const preservedRoles = roster
      .filter(
        (participant) =>
          participant.participation_status === "active" &&
          participant.participant_role !== "student" &&
          !selectedUserIds.has(participant.user_id)
      )
      .map(({ user_id, participant_role }) => ({ user_id, participant_role }));

    return [
      { user_id: form.tutorId, participant_role: "tutor" },
      ...activeStudentIds.map((user_id) => ({ user_id, participant_role: "student" as const })),
      ...preservedRoles,
    ];
  };

  const rosterSignature = (
    participants: Array<Pick<SessionParticipantData, "user_id" | "participant_role">>
  ) =>
    participants
      .map(({ user_id, participant_role }) => `${user_id}:${participant_role}`)
      .sort()
      .join("|");

  const handleCopyIcs = () => {
    if (!form || !session) return;
    const tutorName = getTutorLabel(form.tutorId);
    const studentName = getStudentLabel(form.studentId);

    const startIso = toScheduledAt(form.date, form.startTime);
    const startDate = new Date(startIso);
    const durationMin = Number(form.duration) || 60;
    const endDate = new Date(startDate.getTime() + durationMin * 60 * 1000);

    const pad = (n: number) => `${n}`.padStart(2, "0");
    const formatIcsTime = (d: Date) =>
      `${d.getUTCFullYear()}${pad(d.getUTCMonth() + 1)}${pad(d.getUTCDate())}T${pad(d.getUTCHours())}${pad(d.getUTCMinutes())}00Z`;

    const summary = `rfLearn Session: ${studentName} & ${tutorName}`;
    const description = `rfLearn Session\\nTutor: ${tutorName}\\nStudent: ${studentName}\\nMeeting Link: ${form.meetingUrl || "TBD"}\\nNotes: ${form.notes || "None"}`;

    const icsContent = [
      "BEGIN:VCALENDAR",
      "VERSION:2.0",
      "PRODID:-//rfLearn//Admin Session Manager//EN",
      "CALSCALE:GREGORIAN",
      "METHOD:PUBLISH",
      "BEGIN:VEVENT",
      `UID:${session.id}@rflearn.com`,
      `DTSTAMP:${formatIcsTime(new Date())}`,
      `DTSTART:${formatIcsTime(startDate)}`,
      `DTEND:${formatIcsTime(endDate)}`,
      `SUMMARY:${summary}`,
      `DESCRIPTION:${description}`,
      `URL:${form.meetingUrl || ""}`,
      `LOCATION:${form.location || form.meetingUrl || "Online Virtual Classroom"}`,
      "STATUS:CONFIRMED",
      "END:VEVENT",
      "END:VCALENDAR",
    ].join("\r\n");

    navigator.clipboard
      .writeText(icsContent)
      .then(() => {
        setCopiedIcs(true);
        setTimeout(() => setCopiedIcs(false), 2500);
        toast({
          title: "Calendar invitation copied",
          description: "Standard ICS calendar invite copied to clipboard.",
        });
      })
      .catch(() => {
        toast({
          title: "Unable to copy to clipboard",
          description: "Please check your browser permissions.",
          variant: "destructive",
        });
      });
  };

  const handleLaunchMeeting = () => {
    if (!form?.meetingUrl) {
      toast({
        title: "No meeting URL",
        description: "This session does not have a meeting link configured.",
        variant: "destructive",
      });
      return;
    }
    const targetUrl = form.meetingUrl.startsWith("http")
      ? form.meetingUrl
      : `https://${form.meetingUrl}`;
    window.open(targetUrl, "_blank", "noopener,noreferrer");
  };

  const handleCopyLink = () => {
    if (!form?.meetingUrl) return;
    navigator.clipboard.writeText(form.meetingUrl).then(() => {
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
      toast({
        title: "Meeting link copied",
        description: "Video bridge URL copied to clipboard.",
      });
    });
  };

  const handleConfirmCancel = () => {
    handleChange("status", "cancelled");
    setIsCancelModalOpen(false);
    toast({
      title: "Session status marked as Cancelled",
      description: "Remember to click 'Save Changes' below to persist the cancellation.",
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!sessionId || !form) return;

    if (!form.date || !form.startTime || !form.duration || !form.tutorId) {
      toast({
        title: "Missing session details",
        description: "Date, start time, duration, and tutor are required.",
        variant: "destructive",
      });
      return;
    }

    const desiredRoster = buildDesiredRoster();
    const activeRoster = roster.filter(
      (participant) => participant.participation_status === "active"
    );
    const rosterChanged = rosterSignature(activeRoster) !== rosterSignature(desiredRoster);

    if (rosterChanged && form.status !== "scheduled") {
      toast({
        title: "Roster changes require a scheduled session",
        description: "Set the session status to Scheduled before changing its participant roster.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      await sessionsService.updateSession(sessionId, {
        scheduled_at: toScheduledAt(form.date, form.startTime),
        duration_min: Number(form.duration),
        status: form.status,
        location: form.location || null,
        meeting_url: form.meetingUrl || null,
        tutor_id: form.tutorId,
        student_id: form.studentId || null,
        session_details: {
          notes: form.notes || undefined,
        },
      });

      if (rosterChanged) {
        await sessionsService.replaceSessionParticipants(sessionId, desiredRoster);
      }

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["admin", "session", sessionId] }),
        queryClient.invalidateQueries({ queryKey: ["admin", "session-participants", sessionId] }),
        queryClient.invalidateQueries({ queryKey: ["admin", "sessions"] }),
      ]);

      toast({
        title: "Session updated",
        description: "The session changes have been saved.",
      });
      navigate("/admin/sessions/list");
    } catch (error) {
      toast({
        title: "Unable to update session",
        description: error instanceof Error ? error.message : "Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!sessionId) {
    return (
      <div className="mx-auto max-w-4xl rounded-xl border border-amber-200 bg-amber-50 p-6 text-sm text-amber-800">
        <h3 className="font-semibold text-base mb-1">Session Not Specified</h3>
        <p>Choose a session to edit from the administrative sessions directory.</p>
        <div className="mt-4">
          <Button size="sm" onClick={() => navigate("/admin/sessions/list")}>
            View Sessions
          </Button>
        </div>
      </div>
    );
  }

  if (isLoading || !form) {
    return (
      <div className="mx-auto max-w-4xl rounded-xl border border-slate-200 bg-white p-12 text-center text-slate-500 shadow-sm">
        <Loader2 className="mx-auto mb-3 h-6 w-6 animate-spin text-purple-600" />
        <p className="text-sm font-medium text-slate-700">Loading session record #{sessionId?.slice(0, 8) || "..."}...</p>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="mx-auto max-w-4xl rounded-xl border border-red-200 bg-red-50 p-6 text-sm text-red-700">
        <h3 className="font-semibold text-base mb-1">Session Unavailable</h3>
        <p>Unable to load the requested session record. Please verify permissions or try again.</p>
        <div className="mt-4">
          <Button size="sm" variant="outline" onClick={() => navigate("/admin/sessions/list")}>
            Return to List
          </Button>
        </div>
      </div>
    );
  }

  const sessionHeading =
    session.curriculum_unit?.name ||
    session.batch?.name ||
    `Session: ${getStudentLabel(form.studentId)}`;

  const formattedScheduledDate = form.date
    ? new Date(`${form.date}T00:00:00`).toLocaleDateString(undefined, {
        weekday: "short",
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : "Unscheduled";

  return (
    <div className="mx-auto max-w-5xl space-y-6 pb-12 font-sans antialiased">
      {/* Cancellation Confirmation Modal */}
      <ConfirmationModal
        isOpen={isCancelModalOpen}
        onClose={() => setIsCancelModalOpen(false)}
        title="Cancel Session?"
        description={`Are you sure you want to cancel this session for ${getStudentLabel(form.studentId)} with ${getTutorLabel(form.tutorId)}? This will flag the session as cancelled across student and tutor portals.`}
        primaryAction={{
          label: "Confirm Cancellation",
          onClick: handleConfirmCancel,
          variant: "destructive",
        }}
        secondaryAction={{
          label: "Keep Active",
          onClick: () => setIsCancelModalOpen(false),
          variant: "outline",
        }}
      />

      <Card className="overflow-hidden border border-slate-200 shadow-sm rounded-xl">
        {/* EXECUTIVE LIGHT HEADER */}
        <div className="bg-[#faf9fe] text-slate-900 p-6 md:p-8 border-b border-slate-200">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-5">
            <div>
              <div className="flex items-center gap-2 text-xs font-mono text-purple-700 mb-1.5 font-medium">
                <span>SESSION RECORD</span>
                <span>•</span>
                <span className="text-slate-500 font-normal">ID: {sessionId}</span>
              </div>
              <h2 className="text-2xl font-bold tracking-tight text-slate-900">{sessionHeading}</h2>
              <p className="text-xs md:text-sm text-slate-600 mt-1 flex items-center gap-2">
                <Calendar className="h-3.5 w-3.5 text-slate-500" />
                <span>
                  {formattedScheduledDate} • {form.startTime || "--:--"} ({form.duration} min)
                </span>
              </p>
            </div>

            {/* THREE ACTION BUTTONS WITH OPERATIONAL TOOLTIPS */}
            <TooltipProvider delayDuration={150}>
              <div className="flex flex-wrap items-center gap-2 self-start lg:self-center">
                {/* 1. Copy Calendar ICS Button */}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleCopyIcs}
                      className="h-8 text-xs font-semibold bg-white hover:bg-slate-50 text-slate-700 border-slate-300 hover:border-slate-400 flex items-center gap-1.5 shadow-2xs"
                    >
                      {copiedIcs ? (
                        <Check className="h-3.5 w-3.5 text-emerald-600" />
                      ) : (
                        <Copy className="h-3.5 w-3.5 text-slate-500" />
                      )}
                      <span>{copiedIcs ? "Copied ICS!" : "Copy Calendar ICS"}</span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent
                    side="bottom"
                    className="max-w-xs text-xs p-3 leading-relaxed bg-slate-900 text-slate-100 border border-slate-800 shadow-xl rounded-lg"
                  >
                    Administrators frequently receive requests from students, parents, or tutors
                    asking to resend the calendar invitation for an upcoming session. Generates and
                    copies the .ics calendar invitation payload and session details to the clipboard.
                  </TooltipContent>
                </Tooltip>

                {/* 2. Launch Meeting Button */}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleLaunchMeeting}
                      disabled={!form.meetingUrl}
                      className="h-8 text-xs font-semibold bg-purple-50 hover:bg-purple-100 text-purple-700 border-purple-200 hover:border-purple-300 flex items-center gap-1.5 shadow-2xs disabled:opacity-40"
                    >
                      <ExternalLink className="h-3.5 w-3.5 text-purple-600" />
                      <span>Launch Meeting</span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent
                    side="bottom"
                    className="max-w-xs text-xs p-3 leading-relaxed bg-slate-900 text-slate-100 border border-slate-800 shadow-xl rounded-lg"
                  >
                    Opens the session's active video room in a new browser tab. Allows admins to
                    pre-flight test the video link before lesson start and join as a proctor or
                    moderator if needed.
                  </TooltipContent>
                </Tooltip>

                {/* 3. Cancel Session Button */}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setIsCancelModalOpen(true)}
                      disabled={form.status === "cancelled"}
                      className="h-8 text-xs font-semibold bg-rose-50 hover:bg-rose-100 text-rose-700 border-rose-200 hover:border-rose-300 flex items-center gap-1.5 shadow-2xs disabled:opacity-40"
                    >
                      <XCircle className="h-3.5 w-3.5 text-rose-600" />
                      <span>Cancel Session</span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent
                    side="bottom"
                    className="max-w-xs text-xs p-3 leading-relaxed bg-slate-900 text-slate-100 border border-slate-800 shadow-xl rounded-lg"
                  >
                    Dedicated administrative cancellation workflow. Prompts for confirmation, sets
                    session status to Cancelled, and ensures roster capacity is restored.
                  </TooltipContent>
                </Tooltip>

                {/* Status Indicator Chip */}
                <span
                  className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-semibold border ${getStatusBadgeClass(
                    form.status
                  )} ml-1 uppercase tracking-wide`}
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-current"></span>
                  {form.status}
                </span>
              </div>
            </TooltipProvider>
          </div>

          {/* Quick Metrics Ticker */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6 pt-6 border-t border-slate-200/90 text-xs">
            <div>
              <span className="text-slate-500 block text-xs">Primary Educator</span>
              <span className="text-slate-900 font-semibold truncate block">
                {getTutorLabel(form.tutorId)}
              </span>
            </div>
            <div>
              <span className="text-slate-500 block text-xs">Enrolled Students</span>
              <span className="text-slate-900 font-semibold truncate block">
                {getStudentLabel(form.studentId)}
                {additionalStudentIds.length > 0 ? ` (+${additionalStudentIds.length})` : ""}
              </span>
            </div>
            <div>
              <span className="text-slate-500 block text-xs">Duration / Units</span>
              <span className="text-slate-900 font-semibold block">{form.duration} min (1 Credit)</span>
            </div>
            <div>
              <span className="text-slate-500 block text-xs">Conference Bridge</span>
              <span className="text-purple-700 font-semibold truncate block">
                {form.meetingUrl ? "Online Video Bridge" : "Not configured"}
              </span>
            </div>
          </div>
        </div>

        {/* TABBED INTERACTION BODY */}
        <form id="edit-session-form" onSubmit={handleSubmit}>
          <Tabs defaultValue="schedule" className="w-full">
            <div className="border-b border-slate-200 bg-slate-50 px-6">
              <TabsList className="h-auto p-0 bg-transparent flex items-center gap-2 rounded-none justify-start overflow-x-auto w-full">
                <TabsTrigger
                  value="schedule"
                  className="-mb-px rounded-none border-b-2 border-transparent data-[state=active]:border-purple-600 data-[state=active]:bg-transparent data-[state=active]:text-purple-700 data-[state=active]:font-bold py-3.5 px-4 text-xs font-medium text-slate-600 hover:text-slate-900 transition whitespace-nowrap shadow-none focus-visible:ring-0 focus-visible:outline-none"
                >
                  Schedule & Logistics
                </TabsTrigger>
                <TabsTrigger
                  value="roster"
                  className="-mb-px rounded-none border-b-2 border-transparent data-[state=active]:border-purple-600 data-[state=active]:bg-transparent data-[state=active]:text-purple-700 data-[state=active]:font-bold py-3.5 px-4 text-xs font-medium text-slate-600 hover:text-slate-900 transition whitespace-nowrap shadow-none focus-visible:ring-0 focus-visible:outline-none"
                >
                  Participants & Roster ({participantCount})
                </TabsTrigger>
                <TabsTrigger
                  value="curriculum"
                  className="-mb-px rounded-none border-b-2 border-transparent data-[state=active]:border-purple-600 data-[state=active]:bg-transparent data-[state=active]:text-purple-700 data-[state=active]:font-bold py-3.5 px-4 text-xs font-medium text-slate-600 hover:text-slate-900 transition whitespace-nowrap shadow-none focus-visible:ring-0 focus-visible:outline-none"
                >
                  Curriculum & Tutor Notes
                </TabsTrigger>
                <TabsTrigger
                  value="audit"
                  className="-mb-px rounded-none border-b-2 border-transparent data-[state=active]:border-purple-600 data-[state=active]:bg-transparent data-[state=active]:text-purple-700 data-[state=active]:font-bold py-3.5 px-4 text-xs font-medium text-slate-600 hover:text-slate-900 transition whitespace-nowrap shadow-none focus-visible:ring-0 focus-visible:outline-none"
                >
                  History & Audit Trail
                </TabsTrigger>
              </TabsList>
            </div>

            <CardContent className="p-6 md:p-8">
              {/* TAB 1: SCHEDULE & LOGISTICS */}
              <TabsContent value="schedule" className="space-y-6 mt-0">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Left Column: Timing */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 pb-2 border-b border-slate-100">
                      <Clock className="h-4 w-4 text-purple-600" />
                      <h3 className="text-sm font-bold text-slate-900">Timing & Status</h3>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-1.5">
                        <Label htmlFor="date" className="text-xs font-semibold text-slate-700">
                          Date
                        </Label>
                        <div className="flex">
                          <span className="inline-flex items-center rounded-l-md border border-r-0 border-gray-300 bg-gray-50 px-3 text-gray-500">
                            <Calendar className="h-4 w-4" />
                          </span>
                          <Input
                            id="date"
                            type="date"
                            className="rounded-l-none text-xs"
                            value={form.date}
                            onChange={(e) => handleChange("date", e.target.value)}
                          />
                        </div>
                      </div>

                      <div className="space-y-1.5">
                        <Label htmlFor="time" className="text-xs font-semibold text-slate-700">
                          Start Time
                        </Label>
                        <div className="flex">
                          <span className="inline-flex items-center rounded-l-md border border-r-0 border-gray-300 bg-gray-50 px-3 text-gray-500">
                            <Clock className="h-4 w-4" />
                          </span>
                          <Input
                            id="time"
                            type="time"
                            className="rounded-l-none text-xs"
                            value={form.startTime}
                            onChange={(e) => handleChange("startTime", e.target.value)}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-1.5">
                        <Label htmlFor="duration" className="text-xs font-semibold text-slate-700">
                          Duration
                        </Label>
                        <Select
                          value={form.duration}
                          onValueChange={(value) => handleChange("duration", value)}
                        >
                          <SelectTrigger id="duration" className="text-xs">
                            <SelectValue placeholder="Select duration" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="30">30 minutes</SelectItem>
                            <SelectItem value="45">45 minutes</SelectItem>
                            <SelectItem value="60">1 hour (60 min)</SelectItem>
                            <SelectItem value="90">1.5 hours (90 min)</SelectItem>
                            <SelectItem value="120">2 hours (120 min)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-1.5">
                        <Label htmlFor="status" className="text-xs font-semibold text-slate-700">
                          Session Status
                        </Label>
                        <Select
                          value={form.status}
                          onValueChange={(value) => handleChange("status", value as SessionStatus)}
                        >
                          <SelectTrigger id="status" className="text-xs font-medium">
                            <SelectValue placeholder="Select status" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="scheduled">Scheduled</SelectItem>
                            <SelectItem value="started">Started (In Progress)</SelectItem>
                            <SelectItem value="ended">Ended (Completed)</SelectItem>
                            <SelectItem value="cancelled">Cancelled</SelectItem>
                            <SelectItem value="failed">Failed / No-Show</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* Dual-Timezone Synchronizer Box */}
                    <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs space-y-1.5">
                      <div className="flex items-center gap-1.5 font-semibold text-slate-700">
                        <Info className="h-3.5 w-3.5 text-purple-600" />
                        <span>Synchronized Timezones</span>
                      </div>
                      <div className="flex flex-wrap items-center gap-2 text-slate-600 font-mono text-xs">
                        <span className="bg-white px-2 py-0.5 rounded border border-slate-200">
                          Tutor: {tutorTimezone}
                        </span>
                        <span className="text-slate-400">↔</span>
                        <span className="bg-white px-2 py-0.5 rounded border border-slate-200">
                          Student: {studentTimezone}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Right Column: Connection & Logistics */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 pb-2 border-b border-slate-100">
                      <Video className="h-4 w-4 text-purple-600" />
                      <h3 className="text-sm font-bold text-slate-900">Virtual Bridge & Venue</h3>
                    </div>

                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between">
                        <Label
                          htmlFor="meetingUrl"
                          className="text-xs font-semibold text-slate-700"
                        >
                          Meeting / Video Conference URL
                        </Label>
                        {form.meetingUrl && (
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={handleCopyLink}
                              className="text-xs text-purple-700 hover:text-purple-900 font-medium hover:underline flex items-center gap-1"
                            >
                              <Copy className="h-3 w-3" />
                              {copiedLink ? "Copied" : "Copy"}
                            </button>
                            <button
                              type="button"
                              onClick={handleLaunchMeeting}
                              className="text-xs text-purple-700 hover:text-purple-900 font-medium hover:underline flex items-center gap-1"
                            >
                              <ExternalLink className="h-3 w-3" />
                              Test
                            </button>
                          </div>
                        )}
                      </div>
                      <Input
                        id="meetingUrl"
                        value={form.meetingUrl}
                        onChange={(e) => handleChange("meetingUrl", e.target.value)}
                        placeholder="https://teams.microsoft.com/l/meetup-join/..."
                        className="text-xs font-mono"
                      />
                    </div>

                    <div className="space-y-1.5">
                      <Label htmlFor="location" className="text-xs font-semibold text-slate-700">
                        Physical Room or Location Descriptor
                      </Label>
                      <div className="flex">
                        <span className="inline-flex items-center rounded-l-md border border-r-0 border-gray-300 bg-gray-50 px-3 text-gray-500">
                          <MapPin className="h-4 w-4" />
                        </span>
                        <Input
                          id="location"
                          className="rounded-l-none text-xs"
                          value={form.location}
                          onChange={(e) => handleChange("location", e.target.value)}
                          placeholder="Online Virtual Classroom, Room 204, etc."
                        />
                      </div>
                    </div>

                    <div className="rounded-lg bg-purple-50/70 border border-purple-200 p-3.5 text-xs text-purple-900">
                      <span className="font-semibold block mb-1">Calendar & Attendee Sync:</span>
                      Updates to session timing or meeting links automatically trigger real-time
                      calendar sync events for all active roster participants.
                    </div>
                  </div>
                </div>
              </TabsContent>

              {/* TAB 2: PARTICIPANTS & ROSTER */}
              <TabsContent value="roster" className="space-y-6 mt-0">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Tutor Selection */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between pb-2 border-b border-slate-100">
                      <div className="flex items-center gap-2">
                        <GraduationCap className="h-4 w-4 text-purple-600" />
                        <h3 className="text-sm font-bold text-slate-900">Assigned Educator</h3>
                      </div>
                      <span className="text-xs font-medium text-purple-700 bg-purple-50 px-2 py-0.5 rounded border border-purple-200">
                        Lead Faculty
                      </span>
                    </div>

                    <PersonCommandCombobox
                      id="tutor"
                      value={form.tutorId}
                      onChange={(value) => handleChange("tutorId", value)}
                      options={tutorOptions}
                      placeholder="Search and select a tutor"
                      searchPlaceholder="Search tutor by name, subject, expertise..."
                      emptyLabel={isParticipantsLoading ? "Loading tutors..." : "No tutors found."}
                      createLabel="Create new tutor"
                      icon={<GraduationCap className="h-4 w-4" />}
                      disabled={isParticipantsLoading}
                    />

                    {session?.tutor_profile && (
                      <div className="rounded-lg border border-slate-200 p-3 bg-slate-50 text-xs text-slate-600 space-y-1">
                        <p className="font-semibold text-slate-800">
                          {session.tutor_profile.first_name} {session.tutor_profile.last_name}
                        </p>
                        <p className="text-slate-500 font-mono text-xs">
                          {session.tutor_profile.email}
                        </p>
                        <p className="text-xs text-slate-500">
                          Timezone: {session.tutor_profile.timezone || "US/Eastern"}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Primary Student Selection */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between pb-2 border-b border-slate-100">
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-purple-600" />
                        <h3 className="text-sm font-bold text-slate-900">Primary Student</h3>
                      </div>
                      <span className="text-xs font-medium text-blue-700 bg-blue-50 px-2 py-0.5 rounded border border-blue-200">
                        Billing Owner
                      </span>
                    </div>

                    <PersonCommandCombobox
                      id="student"
                      value={form.studentId}
                      onChange={handlePrimaryStudentChange}
                      options={studentOptions}
                      placeholder="Search and select a student"
                      searchPlaceholder="Search student by name, grade, parent, email..."
                      emptyLabel={
                        isParticipantsLoading ? "Loading students..." : "No students found."
                      }
                      createLabel="Create new student"
                      icon={<User className="h-4 w-4" />}
                      disabled={isParticipantsLoading}
                    />

                    {session?.student_profile && (
                      <div className="rounded-lg border border-slate-200 p-3 bg-slate-50 text-xs text-slate-600 space-y-1">
                        <p className="font-semibold text-slate-800">
                          {session.student_profile.first_name} {session.student_profile.last_name}
                        </p>
                        <p className="text-slate-500 font-mono text-xs">
                          {session.student_profile.email}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Additional Students / Group Roster */}
                <div className="rounded-xl border border-slate-200 p-5 bg-white space-y-4">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pb-3 border-b border-slate-100">
                    <div>
                      <h4 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                        <Users className="h-4 w-4 text-purple-600" />
                        Group Session Roster
                      </h4>
                      <p className="text-xs text-slate-500">
                        Enroll additional students for collaborative group instruction.
                      </p>
                    </div>
                    <span className="text-xs font-semibold text-slate-700 bg-slate-100 px-3 py-1 rounded-full self-start">
                      {participantCount} Total Participant{participantCount === 1 ? "" : "s"}
                    </span>
                  </div>

                  <PersonCommandCombobox
                    id="additional-student"
                    value=""
                    onChange={handleAdditionalStudentChange}
                    options={additionalStudentOptions}
                    placeholder="Search and enroll another student..."
                    searchPlaceholder="Search student by name, grade, parent, email..."
                    emptyLabel={
                      isParticipantsLoading ? "Loading students..." : "No additional students found."
                    }
                    createLabel="Create new student"
                    icon={<User className="h-4 w-4" />}
                    disabled={
                      isParticipantsLoading ||
                      isRosterLoading ||
                      form.status !== "scheduled" ||
                      additionalStudentOptions.length === 0
                    }
                  />

                  {form.status !== "scheduled" && (
                    <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2.5">
                      Session status is currently <strong>{form.status}</strong>. Roster
                      modifications require the session to be in Scheduled status.
                    </p>
                  )}

                  {isRosterLoading ? (
                    <p className="text-xs text-slate-500">Loading roster assignments...</p>
                  ) : additionalStudentIds.length > 0 ? (
                    <div className="space-y-2 pt-2">
                      <p className="text-xs font-semibold text-slate-600">
                        Additional Enrolled Students ({additionalStudentIds.length})
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {additionalStudentIds.map((studentId) => (
                          <span
                            key={studentId}
                            className="inline-flex items-center gap-1.5 rounded-lg bg-blue-50 border border-blue-200 px-3 py-1.5 text-xs text-blue-900 font-medium"
                          >
                            <User className="h-3 w-3 text-blue-600" />
                            {getStudentLabel(studentId)}
                            <button
                              type="button"
                              onClick={() =>
                                setAdditionalStudentIds((current) =>
                                  current.filter((id) => id !== studentId)
                                )
                              }
                              className="rounded-full p-0.5 hover:bg-blue-200/60 transition"
                              disabled={form.status !== "scheduled"}
                              aria-label={`Remove ${getStudentLabel(studentId)}`}
                            >
                              <X className="h-3.5 w-3.5 text-blue-700" />
                            </button>
                          </span>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <p className="text-xs text-slate-500">
                      No additional students assigned (1-on-1 tutoring).
                    </p>
                  )}
                </div>
              </TabsContent>

              {/* TAB 3: CURRICULUM & NOTES */}
              <TabsContent value="curriculum" className="space-y-6 mt-0">
                {/* Linked Unit / Batch Card */}
                {(session?.curriculum_unit || session?.batch) && (
                  <div className="rounded-xl border border-slate-200 p-5 bg-slate-50 space-y-2">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500">
                      Linked Curriculum & Plan
                    </h4>
                    {session.curriculum_unit && (
                      <div className="flex items-center justify-between text-xs">
                        <span className="font-semibold text-slate-800">
                          {session.curriculum_unit.name}
                        </span>
                        <span className="text-slate-500 font-medium">
                          {session.curriculum_unit.domain?.subject?.name}
                        </span>
                      </div>
                    )}
                    {session.batch && (
                      <div className="flex items-center justify-between text-xs pt-1 border-t border-slate-200">
                        <span className="text-slate-600">
                          Batch: {session.batch.name || session.batch.product_name}
                        </span>
                        {session.batch_id && (
                          <span className="text-slate-500 font-mono">
                            ID: {session.batch_id.slice(0, 8)}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="notes" className="text-xs font-semibold text-slate-700">
                    Administrative & Pedagogical Notes
                  </Label>
                  <Textarea
                    id="notes"
                    value={form.notes}
                    onChange={(e) => handleChange("notes", e.target.value)}
                    placeholder="Enter instructions for tutor, curriculum coverage notes, or student accommodations..."
                    className="min-h-[140px] text-xs leading-relaxed"
                  />
                  <p className="text-xs text-slate-500">
                    Visible to system administrators and assigned tutors.
                  </p>
                </div>
              </TabsContent>

              {/* TAB 4: AUDIT TRAIL */}
              <TabsContent value="audit" className="space-y-4 mt-0">
                <div className="rounded-xl border border-slate-200 p-5 bg-white space-y-3 text-xs">
                  <div className="flex items-center justify-between py-1.5 border-b border-slate-100">
                    <span className="text-slate-500 font-medium">Session UUID</span>
                    <span className="font-mono text-slate-800 font-semibold">{sessionId}</span>
                  </div>
                  <div className="flex items-center justify-between py-1.5 border-b border-slate-100">
                    <span className="text-slate-500 font-medium">Created At</span>
                    <span className="font-mono text-slate-700">
                      {session?.created_at
                        ? new Date(session.created_at).toLocaleString()
                        : "Unknown"}
                    </span>
                  </div>
                  <div className="flex items-center justify-between py-1.5 border-b border-slate-100">
                    <span className="text-slate-500 font-medium">Last Updated</span>
                    <span className="font-mono text-slate-700">
                      {session?.updated_at
                        ? new Date(session.updated_at).toLocaleString()
                        : "Unknown"}
                    </span>
                  </div>
                  <div className="flex items-center justify-between py-1.5 border-b border-slate-100">
                    <span className="text-slate-500 font-medium">Creation Source</span>
                    <span className="capitalize font-medium text-slate-700">
                      {session?.created_by || "Admin Portal"}
                    </span>
                  </div>
                  <div className="flex items-center justify-between py-1.5">
                    <span className="text-slate-500 font-medium">Reschedule Count</span>
                    <span className="font-medium text-slate-700">
                      {session?.reschedule_count ?? 0}
                    </span>
                  </div>
                </div>
              </TabsContent>
            </CardContent>
          </Tabs>

          {/* PERSISTENT FOOTER ACTION BAR */}
          <CardFooter className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-6 bg-slate-50 border-t border-slate-200">
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
              <span>Changes made will broadcast calendar sync to active attendees.</span>
            </div>
            <div className="flex items-center gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate("/admin/sessions/list")}
                disabled={isSubmitting}
                className="text-xs"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                form="edit-session-form"
                disabled={isSubmitting}
                className="text-xs flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white font-semibold shadow-xs"
              >
                {isSubmitting ? (
                  <Loader2 size={15} className="animate-spin" />
                ) : (
                  <Save size={15} />
                )}
                Save Session Changes
              </Button>
            </div>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
};

export default EditSession;
